import { QuestionCircleOutlined } from '@ant-design/icons';
import { Switch, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../constants/api-urls';
import { getAuth0AuthHeaders } from '../../utils/auth0Session';
import { verifyAuth0Password } from '../../utils/verifyAuth0Password';
import { isGoogleAuth0User } from '../../utils/isGoogleAuth0User';
import { InputBox } from '..';
import { FilledButtonStyle } from '../../styles/common.style';
import MfaEnrollmentModal from '../MfaEnrollmentModal/MfaEnrollmentModal';
import { AddToPlayListModalStyle } from '../GeneralInformationForm/UsernameModelstyle';

type AccountMfaSettingsProps = {
  userId?: string;
  email?: string;
};

const AccountMfaSettings = ({ userId, email }: AccountMfaSettingsProps) => {
  const [skipMfa, setSkipMfa] = useState(true);
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [mfaEnrollModalOpen, setMfaEnrollModalOpen] = useState(false);
  const [disablePasswordModalOpen, setDisablePasswordModalOpen] = useState(false);
  const [disablePasswordError, setDisablePasswordError] = useState('');
  const [disablePasswordModalKey, setDisablePasswordModalKey] = useState(0);

  const fetchMfaSettings = async () => {
    if (!userId) {
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/auth0/mfa-settings/${userId}`, {
        method: 'GET',
        headers: getAuth0AuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();

        if (data.success && data.data) {
          setSkipMfa(Boolean(data.data.skip_mfa));
        }
      }
    } catch (error) {
      console.error('Failed to fetch MFA settings:', error);
    }
  };

  const disableMfa = async () => {
    if (!userId) {
      return;
    }

    setLoadingMfa(true);
    try {
      const response = await fetch(`${BASE_URL}/auth0/mfa-settings/${userId}`, {
        method: 'PATCH',
        headers: getAuth0AuthHeaders(),
        body: JSON.stringify({
          skip_mfa: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update MFA settings');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update MFA settings');
      }

      setSkipMfa(true);
      toast.success('MFA disabled successfully');
    } catch (error) {
      console.error('Failed to update MFA settings:', error);
      toast.error('Failed to update MFA settings. Please try again.');
    } finally {
      setLoadingMfa(false);
    }
  };

  const resetDisablePasswordModal = () => {
    setDisablePasswordModalOpen(false);
    setDisablePasswordError('');
  };

  const handleDisablePasswordConfirm = async (password: string) => {
    if (!userId) {
      toast.error('Unable to verify password: user session is missing.');
      return;
    }

    if (!password) {
      setDisablePasswordError('Current password is required');
      return;
    }

    setLoadingMfa(true);
    setDisablePasswordError('');

    const verification = await verifyAuth0Password(userId, password, email);

    if (verification.success === false) {
      setDisablePasswordError(verification.message);
      setLoadingMfa(false);
      return;
    }

    resetDisablePasswordModal();
    await disableMfa();
  };

  const handleMfaToggle = (checked: boolean) => {
    if (checked) {
      setMfaEnrollModalOpen(true);
      return;
    }

    if (isGoogleAuth0User(userId)) {
      disableMfa();
      return;
    }

    setDisablePasswordError('');
    setDisablePasswordModalKey(key => key + 1);
    setDisablePasswordModalOpen(true);
  };

  useEffect(() => {
    fetchMfaSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!userId) {
    return null;
  }

  return (
    <>
      <MfaEnrollmentModal
        open={mfaEnrollModalOpen}
        userId={userId}
        email={email}
        onClose={() => setMfaEnrollModalOpen(false)}
        onSuccess={() => {
          setSkipMfa(false);
          setMfaEnrollModalOpen(false);
        }}
      />
      <AddToPlayListModalStyle
        visible={disablePasswordModalOpen}
        footer={null}
        onCancel={resetDisablePasswordModal}
        closable={false}
        destroyOnClose
      >
        {disablePasswordModalOpen ? (
          <Formik
            key={disablePasswordModalKey}
            initialValues={{ currentPassword: '' }}
            onSubmit={values => {
              handleDisablePasswordConfirm(values.currentPassword);
            }}
          >
            {({ setFieldValue }) => (
              <Form className="form-wrapper" autoComplete="off">
                <div>
                  <h1 className="heading">Confirm Password</h1>
                  <p style={{ marginBottom: '16px', opacity: 0.85 }}>
                    Enter your current password to disable MFA.
                  </p>
                </div>
                <InputBox
                  className="passInput"
                  type="password"
                  name="currentPassword"
                  label="Current Password"
                  transparent={true}
                  placeholder=""
                  autoComplete="new-password"
                  showPasswordToggle
                  onChange={event => {
                    setFieldValue('currentPassword', event.target.value);
                    if (disablePasswordError) {
                      setDisablePasswordError('');
                    }
                  }}
                />
              {disablePasswordError ? (
                <span style={{ color: 'red', display: 'block', marginBottom: '12px' }}>
                  {disablePasswordError}
                </span>
              ) : null}
              <div className="footer">
                <FilledButtonStyle
                  background="transparent"
                  width="90px"
                  type="button"
                  onClick={resetDisablePasswordModal}
                  className="btn2"
                >
                  Cancel
                </FilledButtonStyle>
                <FilledButtonStyle
                  type="submit"
                  style={{ padding: '10px 40px', fontSize: '15px' }}
                  disabled={loadingMfa}
                >
                  {loadingMfa ? 'Verifying...' : 'Disable MFA'}
                </FilledButtonStyle>
              </div>
            </Form>
          )}
        </Formik>
        ) : null}
      </AddToPlayListModalStyle>
      <div className="div">
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div className="svgDiv">
            <img alt="security" src="./static/verified.png" />
          </div>
          <p className="ppo">
            {skipMfa ? 'Enable MFA' : 'Disable MFA'}{' '}
            <Tooltip title="If disabled, you will not be required to enter a verification code each time you log in.">
              <QuestionCircleOutlined
                style={{ cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}
              />
            </Tooltip>
          </p>
          <div style={{ marginTop: '6px' }}>
            <Switch
              checked={!skipMfa}
              onChange={handleMfaToggle}
              loading={loadingMfa}
              size="small"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountMfaSettings;
